import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
 // Breadcrumb,
  //BreadcrumbItem,
  Tile,
  Grid,
  Column,
  Dropdown
} from "@carbon/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  ResponsiveContainer,
  Cell
} from "recharts";
import { useUser } from "../../context/UserContext";
import { callApi } from "../../services/apiService";
import { callMongoApi } from "../../services/mongoApiService";

const timeRanges = [
  { id: "1", label: "Today", days: 1 },
  { id: "2", label: "Last 2 Days", days: 2 },
  { id: "4", label: "Last 4 Days", days: 4 },
  { id: "7", label: "Week", days: 7 },
  { id: "30", label: "Month", days: 30 }
];

export default function Dashboard() {
  const { user } = useUser();

  const navigate = useNavigate();

  const [days, setDays] = useState(7);
  const [locations, setLocations] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [topProperties, setTopProperties] = useState([]);
  const [summary, setSummary] = useState({});

  const [gstSummary, setGstSummary] = useState({
  total_transactions: 0,
  total_base_amount: 0,
  total_gst_collected: 0,
  total_final_amount: 0,
});

const handlePropertyClick = (row) => {
  console.log("Clicked row:", row);

  const uuid =
    row?.propertyId ||       // 👈 your API's field name
    row?.propertyUuid ||
    row?._id ||
    row?.id;

  console.log("Resolved uuid:", uuid);

  if (!uuid) {
    console.warn("No property ID found. Row was:", row);
    return;
  }

  navigate(`/settings/property-management/analytics/${uuid}`, {
    state: { propertyTitle: row.propertyTitle },
  });
};
  const COLORS = ["#0f62fe", "#42be65", "#ff832b", "#be95ff"];

const fetchAnalytics = useCallback(async () => {
  const [
    loc,
    types,
    top,
    transactionReport,
    gstReport
  ] = await Promise.all([
    callMongoApi("GET", `useranalyticsreport/locations?days=${days}`),
    callMongoApi("GET", `useranalyticsreport/property-types?days=${days}`),
    callMongoApi("GET", `useranalyticsreport/top-properties?days=${days}`),
    callApi("GET", `/transactions/dashboard/summary`),
    callApi("GET", "/transactions/reports/gst-collection"),
  ]);

  console.log("Locations:", loc);
  console.log("Property Types:", types);
  console.log("Top Properties:", top);

const rawLocations = Array.isArray(loc?.data?.locationViews)
  ? loc.data.locationViews
  : [];

// Normalize village name
const villageLabel = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s.toLowerCase() === "unknown") return null;
  return s;
};

// Aggregate strictly by village
const byVillage = rawLocations.reduce((acc, item) => {
  const village = villageLabel(item.village);
  if (!village) return acc;                 // skip rows without a village
  acc[village] = acc[village] || { village, views: 0 };
  acc[village].views += Number(item.views) || 0;
  return acc;
}, {});

// Sort by views desc so the chart reads top-to-bottom
const locationData = Object.values(byVillage).sort(
  (a, b) => b.views - a.views
);

setLocations(locationData);

  setPropertyTypes(Array.isArray(types?.data) ? types.data : []);
  setTopProperties(Array.isArray(top?.data) ? top.data : []);
  setSummary(transactionReport?.data?.summary || {});
  setGstSummary(gstReport?.data?.summary || {});
}, [days]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  function SummaryCard({ title, value, tooltip }) {
    return (
      <Tile style={{ minHeight: 100 }}>
        <p style={{ fontSize: 14, color: "#6f6f6f" }}>{title}</p>
        <h2 style={{ marginTop: 8 }}>{value}</h2>
        {tooltip && <small style={{ color: "#6f6f6f" }}>{tooltip}</small>}
      </Tile>
    );
  }

  function SectionHeader({ title }) {
    return (
      <Column lg={16} md={8} sm={4} style={{ margin: "2rem 0 1rem" }}>
        <h3>{title}</h3>
      </Column>
    );
  }

  return (
    <Grid className="dashboard-page" fullWidth>
      {/* Header */}
      <Column lg={16} md={8} sm={4}>
       {/* <Breadcrumb noTrailingSlash>
          <BreadcrumbItem>
            <a href="/">Home</a>
          </BreadcrumbItem>
        </Breadcrumb>*/}

        <h1 className="dashboard-page__heading">
          Welcome, <strong>{user?.name || ""}</strong>
        </h1>

        <div style={{ marginTop: "1rem", maxWidth: 300 }}>
          {/* Time range dropdown will go here */}
        </div>
      </Column>

      {/* ================= SUMMARY SECTION ================= */}
      <SectionHeader title="Transaction Summary" />

      {/* Row 1: Basic Stats */}
      <Column lg={4} md={4} sm={4}>
        <SummaryCard
          title="Total Transactions"
          value={summary?.total_transactions || 0}
        />
      </Column>

      <Column lg={4} md={4} sm={4}>
        <SummaryCard
          title="Total Amount"
          value={`₹${(summary?.total_amount || 0).toLocaleString()}`}
        />
      </Column>

      <Column lg={4} md={4} sm={4}>
        <SummaryCard
          title="Successful Transactions"
          value={summary?.successful_transactions_count || 0}
          tooltip={`Amount: ₹${(summary?.successful_transactions_amount || 0).toLocaleString()}`}
        />
      </Column>

      <Column lg={4} md={4} sm={4}>
        <SummaryCard
          title="Failed Transactions"
          value={summary?.failed_transactions_count || 0}
          tooltip={`Amount: ₹${(summary?.failed_transactions_amount || 0).toLocaleString()}`}
        />
      </Column>

      {/* Row 2: Status-wise Stats */}
      <Column lg={4} md={4} sm={4}>
        <SummaryCard
          title="Pending Transactions"
          value={summary?.pending_transactions_count || 0}
          tooltip={`Amount: ₹${(summary?.pending_transactions_amount || 0).toLocaleString()}`}
        />
      </Column>

      <Column lg={4} md={4} sm={4}>
        <SummaryCard
          title="Refunded Transactions"
          value={summary?.refunded_transactions_count || 0}
          tooltip={`Amount: ₹${(summary?.refunded_transactions_amount || 0).toLocaleString()}`}
        />
      </Column>

      {/* Row 3: Property Transactions */}
      <Column lg={4} md={4} sm={4}>
        <SummaryCard
          title="Property Transactions"
          value={summary?.property_transactions_count || 0}
          tooltip={`Amount: ₹${(summary?.property_transactions_amount || 0).toLocaleString()}`}
        />
      </Column>

      <Column lg={4} md={4} sm={4}>
        <SummaryCard
          title="Property % of Total"
          value={`${Number(summary?.property_transactions_percentage_of_total || 0).toFixed(2)}%`}
          tooltip={`Amount %: ${Number(summary?.property_amount_percentage_of_total || 0).toFixed(2)}%`}
        />
      </Column>

      {/* Row 4: Subscription Transactions */}
      <Column lg={4} md={4} sm={4}>
        <SummaryCard
          title="Subscription Transactions"
          value={summary?.subscription_transactions_count || 0}
          tooltip={`Amount: ₹${(summary?.subscription_transactions_amount || 0).toLocaleString()}`}
        />
      </Column>

      <Column lg={4} md={4} sm={4}>
        <SummaryCard
          title="Subscription % of Total"
          value={`${Number(summary?.subscription_percentage_of_total || 0).toFixed(2)}%`}
          tooltip={`Amount %: ${Number(summary?.subscription_amount_percentage_of_total || 0).toFixed(2)}%`}
        />
      </Column>

      <SectionHeader title="GST Summary" />

      <Column lg={4} md={4} sm={4}>
  <SummaryCard
    title="Total GST Transactions"
    value={gstSummary.total_transactions || 0}
  />
</Column>

<Column lg={4} md={4} sm={4}>
  <SummaryCard
    title="Total Base Amount"
    value={`₹${(gstSummary.total_base_amount || 0).toLocaleString()}`}
  />
</Column>

<Column lg={4} md={4} sm={4}>
  <SummaryCard
    title="Total GST Collected"
    value={`₹${(gstSummary.total_gst_collected || 0).toLocaleString()}`}
  />
</Column>

<Column lg={4} md={4} sm={4}>
  <SummaryCard
    title="Total Final Amount"
    value={`₹${(gstSummary.total_final_amount || 0).toLocaleString()}`}
  />
</Column>

      {/* ================= CHARTS SECTION ================= */}
      <SectionHeader title="User Interest Reports" />
      
      <Column lg={16} md={8} sm={4}>
        <div style={{ marginBottom: "1.5rem", maxWidth: 300 }}>
          <Dropdown
            id="range"
            titleText="Time Range"
            label="Select Time Range"
            items={timeRanges}
            selectedItem={timeRanges.find((r) => r.days === days)}
            itemToString={(item) => (item ? item.label : "")}
            onChange={(e) => setDays(e.selectedItem.days)}
          />
        </div>
      </Column>

      {/* LOCATION VIEWS */}
      <Column lg={6} md={4} sm={4}>
        <Tile>
          <h4>Views by Location</h4>
          <ResponsiveContainer width="100%" height={280}>
<BarChart 
  data={locations} 
  layout="vertical" 
  margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
>
  <XAxis type="number" />
  <YAxis 
    dataKey="village" 
    type="category" 
    width={110} // Gives enough room for the village names
    tick={{ fontSize: 12 }} 
  />
  <Tooltip />
  <Bar dataKey="views" fill={COLORS[0]} />
</BarChart>
          </ResponsiveContainer>
        </Tile>
      </Column>

      {/* PROPERTY TYPES */}
<Column lg={6} md={4} sm={4}>
  <Tile>
    <h4>Property Type Interest</h4>
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={Array.isArray(propertyTypes) && propertyTypes.length > 0 ? propertyTypes : []}
          dataKey="views"
          nameKey="propertyType"
          outerRadius={90}
          label
        >
          {Array.isArray(propertyTypes) && propertyTypes.length > 0 ? (
            propertyTypes.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))
          ) : null}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  </Tile>
</Column>

{/* TOP PROPERTIES */}
<Column lg={12} md={8} sm={4}>
  <Tile>
    <h4>Top Viewed Properties</h4>
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={topProperties} layout="vertical">
        <XAxis type="number" />
        <YAxis dataKey="propertyTitle" type="category" width={250} />
        <Tooltip />
        <Bar
          dataKey="views"
          fill="#42be65"
          cursor="pointer"
          onClick={(data) => {
            // Recharts v2 passes the row data directly here
            const row = data?.payload ?? data;
            handlePropertyClick(row);
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  </Tile>
</Column>
    </Grid>
  );
}